// Tabular export — CSV, real .xlsx, and printable PDF for list/report downloads.
// Client-side: builds the file from rows the screen already has.

import { filterOperatorExportColumns } from '@cevop/types';
import type { TableExportColumn, TableExportFormat, TableExportMeta } from './tableExport';
import { exportMetaLines } from './tableExport';
import { buildPdfBytes } from './pdfExport';
import { presentExportMeta, presentExportRows } from './exportFormat';

export type { TableExportColumn, TableExportFormat, TableExportMeta } from './tableExport';
export { exportMetaLines } from './tableExport';
export {
  formatExportAmount,
  formatExportCell,
  formatExportDate,
  presentExportMeta,
  presentExportRows,
} from './exportFormat';
export { buildPdfBytes, formatExportTimestamp } from './pdfExport';

function cell(v: unknown): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Serialise rows (array of objects) to a CSV string using the given column order. */
export function toCsv<T extends Record<string, unknown>>(rows: T[], columns: { key: keyof T; label: string }[]): string {
  const header = columns.map((c) => cell(c.label)).join(',');
  const body = rows.map((r) => columns.map((c) => cell(r[c.key])).join(',')).join('\n');
  return `${header}\n${body}`;
}

export function toCsvWithMeta(
  rows: Record<string, unknown>[],
  columns: TableExportColumn[],
  meta?: TableExportMeta,
): string {
  const operatorColumns = filterOperatorExportColumns(columns);
  const presented = presentExportRows(rows, operatorColumns);
  const presentedMeta = presentExportMeta(meta);
  const table = toCsv(presented, operatorColumns as { key: keyof (typeof presented)[number]; label: string }[]);
  if (!presentedMeta) return table;
  const preamble = exportMetaLines(presentedMeta).map(([label, value]) => `# ${label},${cell(value)}`);
  return `${preamble.join('\n')}\n\n${table}`;
}

/** Trigger a browser download of `content` as `filename`. No-op outside a browser. */
export function downloadCsv(filename: string, content: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const PDF_MIME = 'application/pdf';

function triggerDownload(blob: Blob, filename: string): void {
  if (typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

function xmlEscape(value: string): string {
  return value
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function columnLetter(index: number): string {
  let n = index;
  let label = '';
  while (n >= 0) {
    label = String.fromCharCode((n % 26) + 65) + label;
    n = Math.floor(n / 26) - 1;
  }
  return label;
}

function sheetCellXml(colIndex: number, rowIndex: number, value: unknown): string {
  const ref = `${columnLetter(colIndex)}${rowIndex}`;
  if (typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${ref}"><v>${value}</v></c>`;
  }
  const text = value == null ? '' : String(value);
  return `<c r="${ref}" t="inlineStr"><is><t>${xmlEscape(text)}</t></is></c>`;
}

/** Build a real Office Open XML (.xlsx) workbook as bytes. */
export function buildXlsxBytes(
  rows: Record<string, unknown>[],
  columns: TableExportColumn[],
  meta?: TableExportMeta,
): Uint8Array {
  const operatorColumns = filterOperatorExportColumns(columns);
  const presented = presentExportRows(rows, operatorColumns);
  const presentedMeta = presentExportMeta(meta);
  const sheetRows: string[] = [];
  let rowIndex = 1;

  if (presentedMeta) {
    for (const [label, value] of exportMetaLines(presentedMeta)) {
      sheetRows.push(
        `<row r="${rowIndex}">${sheetCellXml(0, rowIndex, label)}${sheetCellXml(1, rowIndex, value)}</row>`,
      );
      rowIndex += 1;
    }
    sheetRows.push(`<row r="${rowIndex}"></row>`);
    rowIndex += 1;
  }

  sheetRows.push(
    `<row r="${rowIndex}">${operatorColumns.map((column, index) => sheetCellXml(index, rowIndex, column.label)).join('')}</row>`,
  );
  rowIndex += 1;

  presented.forEach((row) => {
    const cells = operatorColumns.map((column, colIndex) => sheetCellXml(colIndex, rowIndex, row[column.key])).join('');
    sheetRows.push(`<row r="${rowIndex}">${cells}</row>`);
    rowIndex += 1;
  });

  const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetRows.join('')}</sheetData></worksheet>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
</Types>`;

  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets><sheet name="Data" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

  const workbookRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
</Relationships>`;

  const encoder = new TextEncoder();
  return zipStore([
    { name: '[Content_Types].xml', data: encoder.encode(contentTypes) },
    { name: '_rels/.rels', data: encoder.encode(rootRels) },
    { name: 'xl/workbook.xml', data: encoder.encode(workbook) },
    { name: 'xl/_rels/workbook.xml.rels', data: encoder.encode(workbookRels) },
    { name: 'xl/worksheets/sheet1.xml', data: encoder.encode(sheetXml) },
  ]);
}

/** CSV, real .xlsx, or PDF export for tabular admin/report downloads. */
export function downloadTable(
  format: TableExportFormat,
  filename: string,
  rows: Record<string, unknown>[],
  columns: TableExportColumn[],
  meta?: TableExportMeta,
): void {
  const base = filename.replace(/\.(csv|xlsx|pdf)$/i, '');
  if (format === 'csv') {
    downloadCsv(base, toCsvWithMeta(rows, columns, meta));
    return;
  }
  if (format === 'pdf') {
    const bytes = buildPdfBytes(rows, columns, meta);
    triggerDownload(new Blob([bytes as BlobPart], { type: PDF_MIME }), `${base}.pdf`);
    return;
  }
  const bytes = buildXlsxBytes(rows, columns, meta);
  triggerDownload(new Blob([bytes as BlobPart], { type: XLSX_MIME }), `${base}.xlsx`);
}

// ── Minimal ZIP (store-only) for Office Open XML packages ─────────────────

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i += 1) {
    let c = i;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[i] = c >>> 0;
  }
  return table;
})();

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i += 1) {
    crc = CRC_TABLE[(crc ^ data[i]!) & 0xff]! ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function u16(value: number): Uint8Array {
  const out = new Uint8Array(2);
  out[0] = value & 0xff;
  out[1] = (value >>> 8) & 0xff;
  return out;
}

function u32(value: number): Uint8Array {
  const out = new Uint8Array(4);
  out[0] = value & 0xff;
  out[1] = (value >>> 8) & 0xff;
  out[2] = (value >>> 16) & 0xff;
  out[3] = (value >>> 24) & 0xff;
  return out;
}

function concat(parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    out.set(part, offset);
    offset += part.length;
  }
  return out;
}

function zipStore(files: Array<{ name: string; data: Uint8Array }>): Uint8Array {
  const encoder = new TextEncoder();
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const file of files) {
    const nameBytes = encoder.encode(file.name);
    const crc = crc32(file.data);
    const local = concat([
      u32(0x04034b50),
      u16(20),
      u16(0),
      u16(0),
      u16(0),
      u16(0),
      u32(crc),
      u32(file.data.length),
      u32(file.data.length),
      u16(nameBytes.length),
      u16(0),
      nameBytes,
      file.data,
    ]);
    locals.push(local);
    centrals.push(
      concat([
        u32(0x02014b50),
        u16(20),
        u16(20),
        u16(0),
        u16(0),
        u16(0),
        u16(0),
        u32(crc),
        u32(file.data.length),
        u32(file.data.length),
        u16(nameBytes.length),
        u16(0),
        u16(0),
        u16(0),
        u16(0),
        u32(0),
        u32(offset),
        nameBytes,
      ]),
    );
    offset += local.length;
  }

  const central = concat(centrals);
  const end = concat([
    u32(0x06054b50),
    u16(0),
    u16(0),
    u16(files.length),
    u16(files.length),
    u32(central.length),
    u32(offset),
    u16(0),
  ]);
  return concat([...locals, central, end]);
}
